import type { AgentAbout, AgentCard, Message } from '@ralph/contracts'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  ArrowUp,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CornerDownLeft,
  Info,
  PanelRightClose,
  PanelRightOpen,
  ShieldCheck,
  Square
} from 'lucide-react'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AssistantMark } from '@/brand/BrandMarks.tsx'
import { Answer } from '@/components/chat/Answer.tsx'
import { ApprovalCard } from '@/components/chat/ApprovalCard.tsx'
import { MessageFeedback } from '@/components/chat/MessageFeedback.tsx'
import { Provenance } from '@/components/chat/Provenance.tsx'
import { AgentAvatar } from '@/components/marketplace/consumer/AgentAvatar.tsx'
import { AgentPicker } from '@/components/marketplace/consumer/AgentPicker.tsx'
import { rememberPreferredAgent } from '@/components/marketplace/consumer/agent-choice.ts'
import {
  conversableAgents,
  dismissUpdateNotice,
  initialUpdateNotice,
  nextUpdateNotice
} from '@/components/marketplace/consumer/agent-identity.ts'
import { FirstUseNoticeDialog } from '@/components/marketplace/consumer/FirstUseNoticeDialog.tsx'
import { ShellHeaderSlot } from '@/components/shell/header-slot.tsx'
import { chatStepCaption } from '@/components/trace/step-caption.ts'
import { TracePanel } from '@/components/trace/TracePanel.tsx'
import { Badge, EmptyState, Spinner } from '@/components/ui/primitives.tsx'
import { queryKeys, useConversations, useMessages, useResolveApproval, useSession } from '@/hooks/use-api.ts'
import { useAcknowledgeNotice, useAgentAbout, useGallery } from '@/hooks/use-marketplace.ts'
import { describeRunFailure, useRunStream } from '@/hooks/use-run-stream.ts'
import { useT } from '@/i18n/index.ts'

/**
 * The conversation surface.
 *
 * Two things left this screen in PRD-18 and one arrived. The model and depth pickers are
 * gone (F-12, AGM-009): they were a consumer-facing control over a policy-plane decision,
 * and worse, the conversation-level `answerMode` could weaken a grounded agent into guessing
 * — the exact restriction the listing page promises the runtime enforces. Response depth is
 * now a profile decision and nothing here sends either field.
 *
 * What arrived is the agent's identity in the header (AGM-008). A conversation is pinned to
 * an agent at creation and cannot be repointed, so "which of these things am I talking to"
 * has to be answerable without navigating — including in history, months later. It is drawn
 * in the shell's top bar via `ShellHeaderSlot`, which puts it in the DOM outside `<main>`:
 * a header is not part of the conversation, and the conversation column is asserted to
 * contain no description of how the agent was configured.
 *
 * That identity is now also the way to every other agent (PRD-19 NAV-020). It stayed a label
 * for a release and read as a broken control: a pill with an identity tile that does nothing.
 * What it opens does not — and cannot — repoint this conversation; it says so, and picking
 * another agent lands on home with that agent chosen, which is the one place in the product a
 * conversation is composed. Nothing is created by opening the menu or by choosing from it.
 *
 * The column reads top to bottom as one account of an answer: the question, who answered,
 * the answer, what it cited, and how it was made. Nothing there is a control over the
 * machine; everything there is a statement about what the machine already did.
 */
export const ChatPage = () => {
  // In the body, never at module scope: this screen is the one a person watches while an
  // answer streams, and a captured pack would keep painting the previous language after a
  // switch with no type error and no failing test (PRD-27 F-3, LOC-012).
  // The whole catalogue, then the two packs this screen reads from it. Whole, because a run
  // that failed is described from `errors`, which is a namespace neither pack contains and
  // which no surface owns — the server names the sentence, the catalogue holds it (D-4).
  const catalogue = useT()
  const t = catalogue.marketplace.gallery
  // The conversation surface's own words. A second pack rather than a merge: `t` above is the
  // marketplace's view of this screen (who is answering, why the composer is closed) and this
  // is the conversation itself — two namespaces, two owners, one body.
  const chatText = catalogue.chat
  const { id: conversationId } = useParams<{ id: string }>()
  const session = useSession()
  const conversations = useConversations()
  const messages = useMessages(conversationId)
  const resolveApproval = useResolveApproval()
  const run = useRunStream()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()

  const conversation = conversations.data?.items.find((c) => c.id === conversationId)
  const about = useAgentAbout(conversation?.agentId)
  // Everything this viewer could talk to instead. Shared cache key with home's listing, so on
  // the common path — home, ask, land here — this is already resolved and costs nothing.
  const gallery = useGallery()

  const [draft, setDraft] = useState('')
  // Open where there is a column to put it in, closed where it would cover the conversation
  // it explains. `lg` is the breakpoint the layout itself switches at; below it the panel is
  // a sheet, and a sheet that opens itself on every landing is a sheet in the way.
  const [showTrace, setShowTrace] = useState(() => window.matchMedia('(min-width: 1024px)').matches)
  const [inspectedRunId, setInspectedRunId] = useState<string | null>(null)
  const [updateNotice, setUpdateNotice] = useState(initialUpdateNotice)
  /**
   * Conversations whose agent this reader can no longer use (PRD-26 D-21).
   *
   * Learned from the server's refusal of a send — `agent_unavailable` — because that is the one
   * authoritative answer: the listing read cannot tell "you lost access" from "this agent has no
   * listing", and a composer disabled on a guess would lock people out of the default agent.
   * Kept per conversation for as long as this page is open, so moving between conversations does
   * not forget which ones are closed to new messages.
   */
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(() => new Set())

  const scrollRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const stickToBottom = useRef(true)

  // Stick to the bottom while streaming, but stop the moment the reader scrolls up — an
  // autoscroll that fights the user is the fastest way to make a streaming UI unusable.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps are the scroll triggers, not every read value
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight
  }, [messages.data, run.state.text, run.state.nodes.length])

  /*
   * The trace stops being a panel and starts being a sheet at `lg`, and the state above is
   * decided once — so a window narrowed after landing left the sheet covering the whole
   * conversation, with no layout holding it and nothing on screen explaining it.
   *
   * Only the narrowing direction acts. Widening deliberately does not reopen: a reader who
   * pressed "Hide trace" has said what they want, and a viewport change is not them changing
   * their mind.
   */
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 1024px)')
    const onChange = (event: MediaQueryListEvent) => {
      if (!event.matches) setShowTrace(false)
    }
    wide.addEventListener('change', onChange)
    return () => wide.removeEventListener('change', onChange)
  }, [])

  /*
   * Entering a conversation: drop whatever belonged to the last one.
   *
   * Keyed to the conversation only — including `run` would re-run on every stream tick and
   * wipe the answer as it arrives — and guarded so that entering the SAME conversation twice
   * is one entry. That guard is load-bearing rather than tidy. StrictMode runs every effect,
   * then its cleanup, then the effect again, and `run.reset()` aborts whatever request is in
   * flight. The auto-send below fires in the first pass, so the second pass was aborting the
   * send it had just started: the server persisted the question and answered it, while the
   * client tore down the stream, handed the question back to the composer, and refetched the
   * messages a few milliseconds before the first one existed. The result was a conversation
   * that looked empty and a question that looked lost, with a completed run behind it.
   */
  const enteredFor = useRef<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: run identity changes per tick
  useEffect(() => {
    if (!conversationId || enteredFor.current === conversationId) return
    enteredFor.current = conversationId
    run.reset()
    setInspectedRunId(null)
    setDraft('')
    setUpdateNotice(initialUpdateNotice)
    composerRef.current?.focus()
  }, [conversationId])

  /*
   * A question arrives as router state and seeds the composer.
   *
   * Two callers, and the difference between them is a governance rule rather than a
   * convenience. A **starter** comes from the listing page and is never sent (AGM-005 abuse
   * case: a one-click starter written by a maker would fire with the reader's own authority
   * before they had read it). The **home composer** sends, because what it carries is the
   * reader's own words and their own press of Ask — the same act as pressing Enter here.
   *
   * The state is cleared immediately so a refresh or a Back navigation does not re-seed, or
   * re-send, a question the user already asked.
   *
   * Declared after the reset effect above on purpose: opening a chat from either changes
   * `conversationId` and the prefill in one render, and the reset would otherwise wipe the
   * draft the prefill just set.
   *
   * Consumed once per conversation, for the same reason the entry above is. Under a double
   * invocation this effect used to re-seed the draft after the send had cleared it, and re-arm
   * the latch the send had just consumed — so the question came back into a composer that had
   * already sent it, and a second send raced the first for the same message sequence. The
   * router state is a handover, and a handover happens once.
   */
  const opening = location.state as { prefill?: string; autoSend?: boolean } | null
  const prefill = opening?.prefill
  const autoSend = opening?.autoSend === true
  const pendingSend = useRef<string | null>(null)
  const openedFor = useRef<string | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate/location identities change per render
  useEffect(() => {
    if (!prefill || openedFor.current === conversationId) return
    openedFor.current = conversationId ?? null
    setDraft(prefill)
    composerRef.current?.focus()
    if (autoSend) pendingSend.current = prefill
    navigate(location.pathname, { replace: true, state: null })
  }, [prefill])

  // "This agent was updated" (AGM-046). The baseline is the listing as it stood when this
  // conversation was opened; the comparison re-runs when the listing is refetched on send.
  // biome-ignore lint/correctness/useExhaustiveDependencies: conversationId re-arms the baseline after the reset above
  useEffect(() => {
    const updatedAt = about.data?.updatedAt
    if (!updatedAt) return
    setUpdateNotice((state) => nextUpdateNotice(state, updatedAt))
  }, [about.data?.updatedAt, conversationId])

  // Hydrate the trace from the most recent answer so opening a past conversation shows how
  // it was produced, not an empty panel. Replay is the same durable event log the live
  // stream writes, so a trace read later is byte-identical to one watched live.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run identity changes per tick
  useEffect(() => {
    if (run.state.runId || run.isStreaming || inspectedRunId) return
    const lastRunId = [...(messages.data?.items ?? [])].reverse().find((m) => m.runId)?.runId
    if (lastRunId) {
      setInspectedRunId(lastRunId)
      void run.replay(lastRunId)
    }
  }, [messages.data, inspectedRunId])

  /*
   * Autosize from the value, not from `onInput`.
   *
   * Three paths set the draft programmatically — a starter chip, the home composer's handoff,
   * and the restore after a refused send — and none of them fires an input event. Measured
   * from a listener instead, a three-line question arrives scrolled out of sight inside a
   * one-line box, which is precisely the text the reader is being asked to check before they
   * send it. The box is also cleared rather than measured when empty, so it shrinks all the
   * way back after a send instead of holding the height the last question needed.
   */
  useEffect(() => {
    const el = composerRef.current
    if (!el) return
    if (draft === '') {
      el.style.height = ''
      return
    }
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 208)}px`
  }, [draft])

  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  const listing = about.data
  const acknowledge = useAcknowledgeNotice()
  const [noticeAck, setNoticeAck] = useState(false)
  // The first-use notice on the chat surface, not only on About (M12): a person who reached
  // this conversation from the sidebar or a direct link never saw the About page, and the
  // default agent can take actions and remember preferences. The server also refuses the send
  // until the ack is recorded (`notice_required`), so this is the friendly half of one gate.
  const noticesDue = listing && !noticeAck ? listing.noticesDue : []
  const refusedHere = run.state.error?.code === 'agent_unavailable'
  const agentUnavailable = refusedHere || (conversationId !== undefined && unavailable.has(conversationId))
  // Only a published listing accepts new runs; archived, suspended and draft states leave
  // the conversation readable and block the composer (AGM-011). A pending first-use notice
  // blocks it too, until acknowledged — and so does an agent this reader can no longer use
  // (PRD-26 D-21), whose conversation stays readable.
  const blocked = (listing !== undefined && listing.state !== 'published') || noticesDue.length > 0 || agentUnavailable

  // Remembered the moment the refusal lands, so the conversation stays closed to new messages after
  // the run state is reset by moving away and back.
  useEffect(() => {
    if (!refusedHere || !conversationId) return
    setUnavailable((current) => (current.has(conversationId) ? current : new Set([...current, conversationId])))
  }, [refusedHere, conversationId])

  const acknowledgeNotices = async () => {
    if (!listing) return
    try {
      for (const capability of listing.noticesDue) {
        await acknowledge.mutateAsync({ agentId: listing.agentId, capability })
      }
      setNoticeAck(true)
      await about.refetch()
    } catch {
      // Leave the dialog open; the server has not recorded the ack, so a retry is honest.
    }
  }

  // When the composer becomes usable — on landing with no notice, or the moment a first-use
  // notice is acknowledged — put the cursor in it so the person can type without reaching for
  // the mouse. The conversation-keyed focus above fires too early when a notice is blocking.
  useEffect(() => {
    if (!blocked) composerRef.current?.focus()
  }, [blocked])

  /*
   * `body` exists for the auto-send path below, which has to hand the question in directly:
   * it fires from an effect in the same commit that seeded the draft, and `draft` is still
   * the previous render's value at that point. Every other caller passes nothing and the
   * composer's own state is used, exactly as before.
   */
  const submit = async (body?: string) => {
    const content = (body ?? draft).trim()
    if (!content || !conversationId || run.isStreaming || blocked) return

    setDraft('')
    setInspectedRunId(null)
    stickToBottom.current = true

    // The question appears in the transcript NOW, not when the answer finishes.
    //
    // `run.send` below resolves when the SSE stream ends — the whole run, which a reasoning
    // model can hold open for ten seconds or more — and the `messages.refetch()` after it is
    // what used to materialise the user's own message. So for the entire run the person was
    // reading a conversation their question was not in: answer streaming, question invisible,
    // arriving only when everything was over. The stream state cannot cover this either — the
    // run's events describe the ASSISTANT's side, and the reducer carries no user content.
    //
    // Appended straight into the query cache rather than held in component state, so every
    // reader of `useMessages` — transcript, sidebar previews — agrees. The temporary id can
    // never collide with a server id, and the entry is short-lived by construction: the
    // refetch after the run replaces the whole page with the durable list, and the rollback
    // below removes it on the one path where the server never recorded the message.
    const optimisticId = `optimistic-${crypto.randomUUID()}`
    const optimistic: Message = {
      id: optimisticId,
      conversationId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
      runId: null,
      citations: [],
      attachments: [],
      feedback: null
    }
    // Seeds the page when the cache has none. A brand-new conversation reaches this line
    // before its first messages fetch has ever resolved, and an updater that only appends to
    // an existing page silently drops the optimistic entry in exactly that case — the FIRST
    // message a person ever sends was the one that stayed invisible.
    queryClient.setQueryData<{ items: Message[]; nextCursor: string | null }>(
      queryKeys.messages(conversationId),
      (page) => (page ? { ...page, items: [...page.items, optimistic] } : { items: [optimistic], nextCursor: null })
    )

    const result = await run.send(conversationId, content)

    // A send the server never accepted — a throttle, an expired session, a dropped
    // connection — leaves nothing on screen to retry from, and the composer was already
    // cleared optimistically. Handing the question back is the difference between a
    // recoverable hiccup and silently eating what someone just typed — and the optimistic
    // bubble comes off the screen with it, because a message the server never recorded must
    // not sit in the transcript looking delivered.
    if (!result.accepted) {
      queryClient.setQueryData<{ items: Message[]; nextCursor: string | null }>(
        queryKeys.messages(conversationId),
        (page) => (page ? { ...page, items: page.items.filter((m) => m.id !== optimisticId) } : page)
      )
      setDraft((current) => (current === '' ? content : current))
    }

    await messages.refetch()
    // Cheap enough to do per send, and it is the moment the update notice is meant to fire:
    // a republish that landed while this conversation was open shows up on the next message.
    void about.refetch()

    // The server names an untitled conversation from its first message and the run updates
    // the running cost, so the sidebar entry is stale the moment a run finishes. Without
    // this the list keeps reading "New conversation" until something else happens to
    // refetch it — usually a page reload.
    await queryClient.invalidateQueries({ queryKey: queryKeys.conversations })
  }

  /*
   * The home composer's send, released once this conversation's listing has resolved.
   *
   * It deliberately waits. `blocked` is false while `about` is still in flight — there is no
   * listing yet to be un-published and no notice yet known to be due — so firing on mount
   * would push a send past the first-use gate, have the server refuse it with
   * `notice_required`, and leave the reader looking at a failed request and a dialog at the
   * same time. Waiting costs one round trip nobody perceives and keeps the gate the only
   * thing that decides.
   *
   * If the listing resolves blocked, the question stays in the composer and the notice
   * dialog or the paused banner explains why — the reader presses send themselves once it
   * clears. The ref is cleared before that branch, so it cannot fire later either — and it is
   * armed exactly once, which is what the guard on the effect above now guarantees.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: submit closes over live state and is stable enough here; the ref latch is what guarantees a single fire
  useEffect(() => {
    const question = pendingSend.current
    if (!question || about.isPending) return
    pendingSend.current = null
    if (blocked) return
    void submit(question)
  }, [about.isPending, blocked])

  const decide = async (
    decision: Parameters<typeof resolveApproval.mutateAsync>[0] extends never
      ? never
      : {
          decision: 'approved' | 'rejected' | 'edited_and_approved'
          digest: string
          editedArguments?: Record<string, unknown>
          note?: string
        }
  ) => {
    const approval = run.state.approval
    if (!approval) return

    const result = await resolveApproval.mutateAsync({ id: approval.approvalId, ...decision })
    // The decision endpoint returns immediately and the run continues detached, so the
    // client reattaches to the durable log rather than waiting on the POST.
    await run.reattach(result.runId)
    await messages.refetch()
  }

  if (!conversationId) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={chatText.noConversationTitle} body={chatText.noConversationBody} />
      </div>
    )
  }

  const history = messages.data?.items ?? []
  // The assistant row for the in-flight run is rendered from the stream, so the persisted
  // (still empty) placeholder is dropped to avoid a duplicate blank bubble.
  //
  // A REPLAY also reports an `assistantMessageId` — of a message that is already persisted
  // and must stay visible. Suppressing it there would silently delete a past answer from
  // the conversation the moment its trace was inspected.
  const suppressedId = run.state.isReplay ? null : run.state.assistantMessageId
  const visible = history.filter((m) => m.id !== suppressedId && m.content.trim() !== '')

  /*
   * Feedback is attached to the question it answered, so the share offer can prefill it
   * (AGM-120). Walking backwards from the answer rather than pairing by index is what keeps
   * that right in a conversation that contains approvals, retries and system turns.
   */
  const questionBefore = (index: number): string => {
    for (let i = index - 1; i >= 0; i -= 1) {
      const message = visible[i]
      if (message?.role === 'user') return message.content
    }
    return ''
  }
  const lastQuestion = questionBefore(visible.length)
  // An agent with no knowledge cannot act on "it could not answer this", so the offer is
  // withheld rather than sent somewhere it would only be noise.
  const agentHasKnowledge = (listing?.disclosure.knowledge.length ?? 0) > 0
  const agentName = listing?.displayName ?? t.chat.unknownAgent

  /*
   * Which answers disclose that memory shaped them (AGM-092).
   *
   * The flag reaches the client once, on `message.completed`, and nothing on the persisted
   * message repeats it — so the run state is the only source, and it describes exactly one
   * answer: the one it names in `assistantMessageId`. Matching on that id rather than
   * assuming "the last one" is what makes the disclosure appear on the right answer when a
   * past run is REPLAYED from the trace, where the streamed block renders nothing at all.
   *
   * // WIRE: this is per-session by construction. Reopening a conversation tomorrow shows no
   * // indicator on yesterday's answers, because `Message` in packages/contracts/src/api.ts
   * // carries no `memoryInfluenced`. If AGM-092 needs the disclosure to survive a reload,
   * // the field has to reach the persisted message too — the client half is then one `||`.
   */
  const memoryInfluencedFor = (messageId: string): boolean =>
    run.state.memoryInfluenced && run.state.assistantMessageId === messageId

  const feedbackFor = (messageId: string, question: string, runId: string | null, given: Message['feedback']) => (
    <MessageFeedback
      messageId={messageId}
      conversationId={conversationId}
      agentId={conversation?.agentId}
      agentHasKnowledge={agentHasKnowledge}
      question={question}
      runId={runId}
      initialVote={given}
      memoryInfluenced={memoryInfluencedFor(messageId)}
    />
  )

  return (
    <div className="relative flex min-h-0 flex-1">
      {listing && noticesDue.length > 0 && (
        <FirstUseNoticeDialog
          agentName={listing.displayName}
          capabilities={[...listing.noticesDue]}
          busy={acknowledge.isPending}
          failed={acknowledge.isError}
          onAcknowledge={() => void acknowledgeNotices()}
          onDismiss={() => navigate(`/agents/${listing.agentId}`)}
        />
      )}

      <ShellHeaderSlot>
        <AgentHeader
          listing={listing}
          agentId={conversation?.agentId}
          options={gallery.data ? conversableAgents(gallery.data) : []}
          onSwitch={(agentId) => {
            // The choice is remembered as well as handed over, so a browser that lands on home
            // with router state stripped — a reload, a restored tab — still opens on the agent
            // this reader just asked for.
            rememberPreferredAgent(agentId)
            navigate('/', { state: { agentId } })
          }}
        />
      </ShellHeaderSlot>

      <div className="flex min-w-0 flex-1 flex-col">
        <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto pb-2 pt-4">
          <div className="mx-auto w-full max-w-[704px] px-5 sm:px-[34px]">
            {visible.length === 0 && !run.state.error && (run.state.isReplay || !run.state.text) && (
              <Opening
                displayName={session.data?.user?.displayName ?? chatText.greetingFallbackName}
                listing={listing}
                onPick={(prompt) => {
                  setDraft(prompt)
                  composerRef.current?.focus()
                }}
              />
            )}

            <div className="space-y-8">
              {visible.map((message, index) =>
                message.role === 'user' ? (
                  <UserTurn key={message.id} content={message.content} />
                ) : (
                  <AssistantTurn
                    key={message.id}
                    content={message.content}
                    citations={message.citations}
                    byline={<AnswerByline agentName={agentName} cited={message.citations.length > 0} />}
                    runId={message.runId}
                    inspected={message.runId !== null && message.runId === inspectedRunId}
                    onInspect={(runId) => {
                      setInspectedRunId(runId)
                      void run.replay(runId)
                      setShowTrace(true)
                    }}
                    /*
                      The panel describes the run the stream currently holds, so it is drawn
                      under the answer that run produced and nowhere else. `isReplay` is the
                      guard that keeps a just-finished live run from rendering its panel twice:
                      that answer is still being rendered from the stream below.
                    */
                    provenance={
                      run.state.isReplay && message.runId !== null && message.runId === run.state.runId ? (
                        <Provenance state={run.state} />
                      ) : null
                    }
                    feedback={feedbackFor(message.id, questionBefore(index), message.runId, message.feedback)}
                  />
                )
              )}

              {/*
                `error` belongs in this gate, not only inside it. A send the server refused
                outright produces no text, no stream and no approval — so without it the
                whole block is skipped and the failure is invisible: the composer clears and
                nothing at all appears. That is the worst possible failure mode for a chat.
                `cancelled` is here for the same reason: Stop pressed before the first token
                would otherwise leave the reader looking at nothing they can explain.
              */}
              {/*
                A send refused because the reader lost the agent (`agent_unavailable`) is not a run
                that failed: nothing ran. It is said once, above the composer it closes, rather than
                as "The run did not finish" over an answer that was never attempted.
              */}
              {!run.state.isReplay &&
                (run.state.text ||
                  run.isStreaming ||
                  run.state.approval ||
                  (run.state.error && !refusedHere) ||
                  run.state.status === 'cancelled') && (
                  <div className="space-y-4">
                    {/* Only over something that is an answer. A send the server refused
                        outright produces an error and nothing else, and "X answered" above
                        it would be the surface telling the reader something untrue. */}
                    {(run.state.text || run.isStreaming) && (
                      <AnswerByline agentName={agentName} cited={run.state.citations.length > 0} />
                    )}

                    {run.state.thinking && run.isStreaming && !run.state.text && (
                      <p className="readout flex items-center gap-2 text-fg-subtle">
                        <Spinner size={11} />
                        {chatText.thinking}
                      </p>
                    )}

                    {run.state.text && (
                      <Answer content={run.state.text} citations={run.state.citations} streaming={run.isStreaming} />
                    )}

                    {/*
                      What the run is doing, in the reader's words — not the server's. The step
                      carries its caption as a code now, so this renders from the catalogue
                      (PRD-27 D-8); before it did, it printed `run_steps.label` verbatim, which
                      is English prose naming the model profile the deployment chose and is the
                      one thing this column does not say (AGM-022). A step whose code this build
                      does not know says the generic thing instead — the operator's line is in
                      the trace panel, in full, where it belongs.
                    */}
                    {run.isStreaming && !run.state.text && !run.state.thinking && (
                      <p className="readout flex items-center gap-2 text-fg-subtle">
                        <Spinner size={11} />
                        {chatStepCaption(chatText, run.state.nodes.at(-1)) ?? chatText.starting}
                      </p>
                    )}

                    {run.state.status === 'cancelled' && <p className="readout text-fg-subtle">{chatText.stopped}</p>}

                    {run.state.approval && (
                      <ApprovalCard approval={run.state.approval} onDecide={decide} busy={resolveApproval.isPending} />
                    )}

                    <Provenance state={run.state} />

                    {/*
                      The answer that just finished is rendered from the stream, not from the
                      persisted list (its row is suppressed above), so it needs its own
                      feedback row — otherwise the one answer a person is most likely to rate
                      is the only one they cannot.
                    */}
                    {!run.isStreaming &&
                      run.state.text &&
                      run.state.assistantMessageId &&
                      feedbackFor(run.state.assistantMessageId, lastQuestion, run.state.runId, null)}

                    {run.state.error && !refusedHere && (
                      <div className="rounded-card border border-alert/40 bg-alert-soft px-4 py-3">
                        <p className="text-[13.5px] leading-relaxed">
                          <span className="font-semibold">{chatText.runFailed}</span>{' '}
                          {describeRunFailure(catalogue, run.state.error)}
                        </p>
                        <p className="readout mt-1 text-fg-subtle">{run.state.error.code}</p>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>

        {/*
          The answer is announced once, when it is finished (AGM-124). A live region on the
          streaming text itself would re-announce on every token and make a screen reader
          unusable for the duration of the answer, which is the opposite of assistance.
        */}
        <p aria-live="polite" aria-atomic="true" className="sr-only">
          {!run.isStreaming && !run.state.isReplay && run.state.text ? run.state.text : ''}
        </p>

        <div className="px-5 pb-4 pt-2 sm:px-[34px]">
          <div className="mx-auto w-full max-w-[704px]">
            {updateNotice.show && listing && (
              <p className="mb-2 flex flex-wrap items-center gap-1.5 text-[12.5px] text-fg-muted">
                <Info size={12} className="shrink-0" aria-hidden />
                <span>{t.chat.updatedNotice}</span>
                <Link to={`/agents/${listing.agentId}`} className="underline underline-offset-2">
                  {t.chat.updatedNoticeLink}
                </Link>
                <button
                  type="button"
                  onClick={() => setUpdateNotice(dismissUpdateNotice)}
                  className="readout ml-auto text-fg-subtle transition-colors hover:text-fg"
                >
                  {t.chat.dismissUpdate}
                </button>
              </p>
            )}

            {agentUnavailable && (
              <p role="status" className="mb-2 flex items-start gap-1.5 text-[12.5px] leading-snug text-alert">
                <Info size={12} className="mt-0.5 shrink-0" aria-hidden />
                {t.chat.unavailableComposer(listing?.displayName ?? null)}
              </p>
            )}

            {!agentUnavailable && listing && listing.state !== 'published' && (
              <p className="mb-2 flex items-start gap-1.5 text-[12.5px] leading-snug text-alert">
                <Info size={12} className="mt-0.5 shrink-0" aria-hidden />
                {/* Three reasons a listing refuses new work, and they are not interchangeable.
                    `draft` used to fall through to "has been retired", which told the owner of
                    an agent they had just unpublished that it was gone for good.

                    Gated on the STATE, not on `blocked`. A pending first-use notice also
                    blocks the composer, and it is not a listing state — so reading `blocked`
                    here sent every reader of a perfectly healthy agent through the same
                    fall-through, and the first thing anyone saw on a fresh deployment was
                    "this agent has been retired" in red under the notice explaining how to
                    start using it. The notice raises its own dialog and says its own piece;
                    this line is only ever about the listing. */}
                {listing.state === 'suspended'
                  ? t.chat.suspendedComposer
                  : listing.state === 'draft'
                    ? t.chat.draftComposer
                    : t.chat.archivedComposer}
              </p>
            )}

            {/*
              The composer wears the iridescent edge — one of the three places in the product
              that gradient is allowed — because it is where the person acts. It is a single
              surface rather than a bordered field on a panel: the whole card is the input, so
              the focus ring belongs to the card and is suppressed on the textarea inside it.
            */}
            <div className="relative">
              <div aria-hidden className="absolute -inset-px rounded-[20px] bg-[image:var(--irid)] opacity-50" />
              <div className="relative rounded-panel bg-card-solid px-4 pb-2.5 pt-3 shadow-glass transition-shadow focus-within:shadow-[var(--ring),var(--glass)]">
                <textarea
                  ref={composerRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      void submit()
                    }
                  }}
                  rows={1}
                  disabled={blocked}
                  placeholder={t.chat.composerPlaceholder}
                  aria-label={chatText.composerLabel}
                  className="max-h-52 min-h-[38px] w-full resize-none bg-transparent text-[14.5px] leading-[1.55] placeholder:text-fg-subtle focus:outline-none focus-visible:shadow-none disabled:opacity-50"
                />

                <div className="flex items-center gap-2">
                  <span className="readout ml-auto hidden items-center gap-1.5 text-[9.5px] text-fg-subtle sm:flex">
                    <CornerDownLeft size={10} aria-hidden />
                    {chatText.composerSendHint}
                  </span>

                  {run.isStreaming ? (
                    <button
                      type="button"
                      onClick={run.cancel}
                      aria-label={chatText.stopGenerating}
                      title={chatText.stopGenerating}
                      className="flex h-[31px] w-[31px] shrink-0 items-center justify-center rounded-full border border-rule-strong bg-card-solid text-fg transition-[transform,box-shadow] duration-200 hover:-translate-y-px hover:shadow-glass"
                    >
                      <Square size={11} strokeWidth={2.6} aria-hidden />
                    </button>
                  ) : (
                    <button
                      type="button"
                      // Wrapped rather than passed directly: `submit` now takes an optional
                      // body, and a bare handler reference would hand it the click event.
                      onClick={() => void submit()}
                      disabled={!draft.trim() || blocked}
                      aria-label={chatText.sendMessage}
                      title={chatText.sendMessage}
                      className="flex h-[31px] w-[31px] shrink-0 items-center justify-center rounded-full bg-[image:var(--irid)] text-white shadow-[var(--irid-glow)] transition-transform duration-200 hover:-translate-y-px disabled:pointer-events-none disabled:opacity-45"
                    >
                      <ArrowUp size={13} strokeWidth={2.4} aria-hidden />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-2 flex">
              <button
                type="button"
                onClick={() => setShowTrace((v) => !v)}
                className="readout ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-fg-subtle transition-colors hover:bg-card hover:text-fg"
              >
                {showTrace ? <PanelRightClose size={13} aria-hidden /> : <PanelRightOpen size={13} aria-hidden />}
                {showTrace ? chatText.hideTrace : chatText.showTrace}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showTrace && <TracePanel state={run.state} onClose={() => setShowTrace(false)} />}
    </div>
  )
}

/**
 * Who is answering (AGM-008), in the shell's top bar — and the way to everyone else.
 *
 * Degrades rather than disappears: a listing this reader cannot see — an admin-only agent, a
 * conversation pinned to something since retired — still gets a pill, because "no pill" is
 * indistinguishable from "the default agent" and that ambiguity is what F-10 was.
 *
 * Two controls, side by side inside one pill, and they are siblings rather than nested for the
 * obvious reason: a link inside a menu button is a link nobody can reach.
 *
 * - The identity opens the picker, and it opens whenever the gallery has answered with anything
 *   at all — including a list of one, which is what a fresh deployment has. It used to require
 *   something to switch *to*, which made the pill a plain label on exactly the installations
 *   where it is most prominent. A menu that says "this is the only agent shared with you, and
 *   here is where the others come from" is a better answer than a control that does nothing.
 * - "About this agent" stays visible. It answers a different question — *what is this thing* —
 *   and burying it inside the switcher would trade a one-click answer for a two-click one on
 *   the surface where trust is decided.
 *
 * The link's visible word is "about" and its accessible name is the whole phrase, so the pill
 * stays one 33px line while a screen reader (and the browser suite) still get "About this
 * agent".
 */
const AgentHeader = ({
  listing,
  agentId,
  options,
  onSwitch
}: {
  listing: AgentAbout | undefined
  agentId: string | undefined
  options: AgentCard[]
  onSwitch: (agentId: string) => void
}) => {
  const t = useT().marketplace.gallery
  const chatText = useT().chat
  const name = listing?.displayName ?? t.chat.unknownAgent
  const identity = (
    <>
      {listing ? (
        <AgentAvatar displayName={listing.displayName} icon={listing.icon} accent={listing.accent} size="sm" />
      ) : (
        <span aria-hidden className="h-[21px] w-[21px] shrink-0 rounded-[7px] bg-card-solid" />
      )}
      <span className="eyebrow hidden shrink-0 sm:inline">{t.chat.agentLabel}</span>
      <span className="min-w-0 truncate text-[12px] font-semibold tracking-[-0.014em]">{name}</span>
      {/* Wrapped rather than given a `hidden` class of its own: `Badge` sets `inline-flex`,
          and two display utilities in the same layer would decide the pill by stylesheet
          order rather than by intent. */}
      {listing?.official && (
        <span className="hidden shrink-0 md:inline-flex">
          <Badge tone="grounded">
            <ShieldCheck size={8} strokeWidth={3} aria-hidden />
            {chatText.official}
          </Badge>
        </span>
      )}
    </>
  )

  return (
    <div className="fade-in flex h-[33px] min-w-0 items-center gap-1 rounded-full border border-rule bg-card pl-[5px] pr-[5px]">
      {/*
        The static form survives for one state only: the gallery has not answered, or could not.
        There is then nothing to put in a menu and no gallery worth sending anybody to, and the
        About link beside this still works. Every state where the listing resolved gets the
        control.
      */}
      {options.length > 0 ? (
        <AgentPicker
          agents={options}
          currentId={agentId ?? null}
          onPick={onSwitch}
          align="end"
          triggerLabel={t.picker.chatTrigger(name)}
          title={t.picker.chatTitle}
          hint={
            // Only one agent, and it is this conversation's: there is nothing to pick, so the
            // hint states the rule instead of dangling an alternative that does not exist.
            options.length === 1 && options[0]?.agentId === agentId
              ? `${t.picker.chatHintAlone(name)} ${t.picker.alone}`
              : t.picker.chatHint(name)
          }
          currentLabel={t.picker.chatCurrent}
          triggerClassName="flex h-[27px] min-w-0 items-center gap-2 rounded-full px-1.5 text-fg transition-colors duration-200 hover:bg-card-solid"
          trigger={
            <>
              {identity}
              <ChevronDown size={11} strokeWidth={2.2} className="shrink-0 text-fg-subtle" aria-hidden />
            </>
          }
        />
      ) : (
        <span className="flex h-[27px] min-w-0 items-center gap-2 px-1.5">{identity}</span>
      )}

      {agentId && listing && (
        <Link
          to={`/agents/${agentId}`}
          aria-label={t.chat.aboutLink}
          className="readout inline-flex h-[23px] shrink-0 items-center gap-1 rounded-full px-2 text-[9.5px] text-fg-muted transition-colors hover:bg-card-solid hover:text-fg"
        >
          {chatText.aboutShort}
          <ChevronRight size={9} strokeWidth={2.4} aria-hidden />
        </Link>
      )}
    </div>
  )
}

/**
 * The byline over an answer.
 *
 * The orb marks the model's own presence — one of exactly three places it is allowed to
 * appear. The trust mark is a statement about THIS answer (it cites something) rather than
 * about the agent's configuration, and it never travels alone: the sources it refers to are
 * listed immediately underneath.
 */
const AnswerByline = ({ agentName, cited }: { agentName: string; cited: boolean }) => {
  const chatText = useT().chat

  return (
    <div className="mb-3 flex items-center gap-2.5">
      <AssistantMark size={22} />
      {/* Truncates rather than pushing the badge off the column: an agent can be called
          anything, and the mark at the end is the half a reader is looking for. */}
      <span className="eyebrow min-w-0 truncate text-fg-muted">{chatText.answeredBy(agentName)}</span>
      <span aria-hidden className="h-px min-w-3 flex-1 bg-rule" />
      {cited && (
        <Badge tone="grounded" className="shrink-0">
          <CircleCheck size={9} strokeWidth={3} aria-hidden />
          {chatText.cited}
        </Badge>
      )}
    </div>
  )
}

/**
 * A past answer.
 *
 * Clicking "show trace" loads its run into the panel. That turns the trace from a live-only
 * curiosity into an audit surface: "why did it say that, three weeks ago" is answerable
 * without leaving the conversation — and it is also what brings this answer's provenance
 * panel up, since both read the same replayed run.
 */
const AssistantTurn = ({
  content,
  citations,
  byline,
  runId,
  inspected,
  onInspect,
  provenance,
  feedback
}: {
  content: string
  citations: Message['citations']
  /** Built by the page, which is the only thing that knows which agent is answering. */
  byline: ReactNode
  runId: string | null
  inspected: boolean
  onInspect: (runId: string) => void
  provenance: ReactNode
  /** The rating row, built by the page so this component stays a renderer. */
  feedback: ReactNode
}) => {
  const chatText = useT().chat

  return (
    <div className="group">
      {byline}
      <Answer content={content} citations={citations} />
      {provenance}
      {runId && !provenance && (
        <div className="mt-3 flex justify-end">
          {/* Sits in its own row rather than floating over the prose: an absolute control in
              the top-right corner overlaps the first line of any answer that reaches the
              full column width, which most of them do. */}
          <button
            type="button"
            onClick={() => onInspect(runId)}
            aria-pressed={inspected}
            className={clsx(
              'readout rounded-full px-2 py-1 text-[10px] transition-opacity hover:bg-card hover:text-fg',
              inspected
                ? 'text-primary opacity-100'
                : 'text-fg-subtle opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
            )}
          >
            {inspected ? chatText.inspecting : chatText.inspect}
          </button>
        </div>
      )}
      {feedback}
    </div>
  )
}

/**
 * The question, in the one other place the iridescent gradient is allowed: what the person
 * themselves said. The asymmetric corner points back at the composer it came from.
 */
const UserTurn = ({ content }: { content: string }) => (
  <div className="flex justify-end">
    <div className="max-w-[80%] whitespace-pre-wrap rounded-[20px_20px_7px_20px] bg-[image:var(--irid)] px-[17px] py-[11px] text-[14.5px] leading-[1.58] tracking-[-0.008em] text-white shadow-[var(--irid-glow)]">
      {content}
    </div>
  </div>
)

/**
 * The empty conversation.
 *
 * The suggestions are the agent's own starters now, not four hardcoded questions about a
 * knowledge base that may not exist on this deployment. An agent with no starters gets no
 * list rather than a generic one — a prompt the agent cannot answer is worse than silence.
 */
const Opening = ({
  displayName,
  listing,
  onPick
}: {
  displayName: string
  listing: AgentAbout | undefined
  onPick: (prompt: string) => void
}) => {
  const t = useT().marketplace.gallery.chat

  return (
    <div className="fade-in mb-10">
      <h1 className="display text-[30px] sm:text-[38px]">
        {t.openingGreeting(displayName.split(' ')[0] ?? displayName)}
      </h1>
      <p className="mt-2 max-w-lg text-[13.5px] leading-relaxed text-fg-muted">
        {listing?.tagline || listing?.description || t.openingFallback}
      </p>

      {listing && listing.starters.length > 0 && (
        <ul className="mt-6 grid gap-2 sm:grid-cols-2">
          {listing.starters.map((prompt, index) => (
            <li key={prompt} className="rise" style={{ animationDelay: `${Math.min(index, 6) * 50}ms` }}>
              <button
                type="button"
                onClick={() => onPick(prompt)}
                className={clsx(
                  'h-full w-full rounded-tile border border-rule bg-card px-3.5 py-2.5 text-left',
                  'text-[13px] leading-snug tracking-[-0.008em] text-fg-muted',
                  'transition-[transform,color,border-color,box-shadow] duration-200',
                  'hover:-translate-y-0.5 hover:border-rule-strong hover:text-fg hover:shadow-glass'
                )}
              >
                {prompt}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
